'use client'

import * as React from 'react'

import { cn } from '../../../lib/utils'
import { Icon, iconList, type iconTypes } from '../Icon'
import { Command, CommandEmpty, CommandInput, CommandList } from '../Command'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'
import MetafieldLabel from '../MetafieldLabel'
import { FieldChange } from '../FieldChange'

export type IconSelectorProps = {
  value: iconTypes | null
  onValueChange: (next: iconTypes | null) => void
  placeholder?: string
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  sideOffset?: number
  alignOffset?: number
  contentClassName?: string
  buttonClassName?: string
  // Form-mode props
  id?: string
  label?: string
  labelClassName?: string
  helpText?: string
  errorMessage?: string
  originalValue?: iconTypes | null
  isDisabled?: boolean
  metafield?: {
    label: string
    href: string
  }
}

const ITEM_HEIGHT = 36
const LIST_HEIGHT = 320
const OVERSCAN = 12

function formatIconName(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
}

export function IconSelector({
  value,
  onValueChange,
  placeholder = 'Select icon...',
  open,
  defaultOpen,
  onOpenChange,
  sideOffset = 4,
  alignOffset = 0,
  contentClassName,
  buttonClassName,
  id,
  label,
  labelClassName,
  helpText,
  errorMessage,
  originalValue,
  isDisabled,
  metafield,
}: IconSelectorProps) {
  const isOpenControlled = open !== undefined
  const [internalOpen, setInternalOpen] = React.useState<boolean>(
    defaultOpen ?? false,
  )
  const resolvedOpen = isOpenControlled ? open : internalOpen
  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isOpenControlled) setInternalOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [isOpenControlled, onOpenChange],
  )

  const [search, setSearch] = React.useState<string>('')
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return iconList
    return iconList.filter((n) => n.toLowerCase().includes(q))
  }, [search])

  const total = filtered.length
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const [scrollTop, setScrollTop] = React.useState<number>(0)
  const [viewportHeight, setViewportHeight] =
    React.useState<number>(LIST_HEIGHT)

  React.useEffect(() => {
    const node = listRef.current
    if (!node) return
    const resize = () => setViewportHeight(node.clientHeight || LIST_HEIGHT)
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(node)
    return () => {
      ro.disconnect()
    }
  }, [resolvedOpen])

  const onScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop)
  }, [])

  const totalHeight = total * ITEM_HEIGHT
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN)
  const endIndex = Math.min(
    total,
    Math.ceil((scrollTop + viewportHeight) / ITEM_HEIGHT) + OVERSCAN,
  )
  const offsetTop = startIndex * ITEM_HEIGHT
  const visibleItems = React.useMemo(
    () => filtered.slice(startIndex, endIndex),
    [filtered, startIndex, endIndex],
  )

  const selectedLabel = value ? formatIconName(value) : null

  const normalizeValue = React.useCallback(
    (val: iconTypes | null | undefined): string => {
      if (val === null || val === undefined) return '( None )'
      return formatIconName(val as string)
    },
    [],
  )

  const shouldShowFieldChange =
    originalValue !== undefined &&
    normalizeValue(originalValue) !== normalizeValue(value)

  const isFormMode = !!(
    label ||
    helpText ||
    errorMessage ||
    metafield ||
    originalValue !== undefined
  )

  const triggerContent = (
    <div className="flex items-center gap-2">
      {value ? <Icon name={value} size={16} /> : null}
      <span
        className={cn(!selectedLabel ? 'text-muted-foreground' : undefined)}
      >
        {selectedLabel ?? placeholder}
      </span>
    </div>
  )

  const triggerNode = (
    <button
      type="button"
      disabled={isDisabled}
      className={cn(
        'inline-flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50',
        buttonClassName,
      )}
      onClick={() => handleOpenChange(!resolvedOpen)}
    >
      {triggerContent}
      <Icon name="ChevronsUpDown" className="size-4 shrink-0 opacity-50" />
    </button>
  )

  const legacyFormTriggerNode = isFormMode ? (
    <button
      type="button"
      disabled={isDisabled}
      className={cn(
        'text-primary cursor-text flex items-center justify-between h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        buttonClassName,
      )}
      onClick={() => handleOpenChange(!resolvedOpen)}
    >
      {triggerContent}
      <Icon name="ChevronsUpDown" />
    </button>
  ) : null

  const dropdown = (
    <PopoverContent
      align="start"
      side="bottom"
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      className={cn('w-[320px] p-0', contentClassName)}
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder="Search icons..."
          onValueChange={(v) => setSearch(v)}
        />
        <CommandList
          ref={listRef as unknown as React.Ref<HTMLDivElement>}
          onScroll={onScroll}
          className="max-h-[320px] overflow-y-auto"
          style={{ height: LIST_HEIGHT }}
        >
          {total === 0 ? (
            <CommandEmpty>No results.</CommandEmpty>
          ) : (
            <div style={{ height: totalHeight, position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  top: offsetTop,
                  left: 0,
                  right: 0,
                }}
              >
                {visibleItems.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-2 px-2 text-sm hover:bg-accent text-left',
                      value === name ? 'bg-accent' : undefined,
                    )}
                    style={{ height: ITEM_HEIGHT }}
                    onClick={() => {
                      const next = value === name ? null : name
                      onValueChange(next)
                      handleOpenChange(false)
                    }}
                  >
                    <Icon name={name} size={16} />
                    <span className="truncate flex-1 text-left">
                      {formatIconName(name)}
                    </span>
                    {value === name ? <Icon name="Check" /> : null}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CommandList>
      </Command>
    </PopoverContent>
  )

  if (!isFormMode) {
    return (
      <Popover open={resolvedOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{triggerNode}</PopoverTrigger>
        {dropdown}
      </Popover>
    )
  }

  return (
    <div className="bg-background w-full">
      <label
        className={cn({
          'text-destructive opacity-100 leading-tight w-full': !!errorMessage,
        })}
        htmlFor={id}
      >
        {label && (
          <div
            className={cn(
              'flex justify-between items-center text-muted-foreground mb-1 h-5',
              labelClassName,
            )}
          >
            {label}
            <div className="flex items-center gap-2">
              {shouldShowFieldChange && (
                <FieldChange
                  oldValue={normalizeValue(originalValue)}
                  newValue={normalizeValue(value)}
                  size="sm"
                  handleRevert={() => {
                    onValueChange(originalValue ?? null)
                  }}
                />
              )}
              {metafield && <MetafieldLabel {...metafield} />}
            </div>
          </div>
        )}
        <Popover open={resolvedOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>{legacyFormTriggerNode}</PopoverTrigger>
          {dropdown}
        </Popover>
        {helpText && (
          <div className="text-muted-foreground normal-case text-xs mt-2">
            {helpText}
          </div>
        )}
        {errorMessage && (
          <div className={cn('text-destructive text-xs mt-2')}>
            {errorMessage}
          </div>
        )}
      </label>
    </div>
  )
}

export default IconSelector
